/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import './ShareCardMenu.styles.scss';

import IconShare from '../icons/IconShare';
import { CloseButton } from '../buttons/Buttons';

import { getLocalText, LocalText } from '../../../utilities/localizedUtil';
import { I18nImage } from '../I18nTranslation';
import { useUIState } from '../../hooks/useUIState';
import { useActiveUser } from '../../hooks/useUser';
import { useShareCardDefinitionListForActiveProfile } from '../../hooks/useShareCard';
import { useNavigate } from '../../hooks/useNavigate';
import RouteNames from '../../appAuthorized/RouteNames';

const createDestinationUrl = (definitionId, colorId) => {
  return RouteNames.createShareCard(definitionId) + (colorId ? `?colorId=${colorId}` : '');
};

const ShareCardMenu: React.FC = () => {
  const [{ preferredLanguageCode }] = useActiveUser();
  const [, uiActions] = useUIState();
  const [{ shareCardDefinionList }, scActions] = useShareCardDefinitionListForActiveProfile();
  const [, navActions] = useNavigate();

  const [isLoading, setIsLoading] = React.useState(false);

  const availableDefinitions = shareCardDefinionList
    .filter(def => !def.hidden && def.allow_creation)
    .sort((a, b) => getLocalText(a.name || a.title, preferredLanguageCode).localeCompare(getLocalText(b.name || b.title, preferredLanguageCode)));

  return (
    <div className="cmp-add-menu">
      <div className="container">
        {
          availableDefinitions.map((definition) =>
            <div
              className="add-item"
              key={definition.id}
              onClick={() => {
                if (isLoading) { return null; }

                const definitionId = definition.id;
                if (!definition.color) {
                  setIsLoading(true);
                  scActions.apiGetShareCardColor()
                    .then((newColor: any) => {
                      uiActions.dismissModal();
                      const url = createDestinationUrl(definitionId, newColor.id);
                      navActions.goTo(url);
                    }).catch(() => {
                      uiActions.dismissModal();
                    });
                } else {
                  uiActions.dismissModal();
                  navActions.goTo(createDestinationUrl(definitionId, null));
                }
              }}
            >
              <div className="cmp-additem-sharecard">
                <div className="title-row">
                  <div className="font-title-3 font-color-profile-accent">
                    <LocalText text={definition.name || definition.title}/>
                  </div>

                  <div className="icon">
                    {
                      definition.icon
                        ? <I18nImage data={definition.icon.url_dark} />
                        : <IconShare.Medium color="gsMiddle" />
                    }
                  </div>
                </div>

                <div className="font-footnote font-color-gsDark">
                  <LocalText text={definition.description}/>
                </div>
              </div>
            </div>
          )
        }
      </div>
      <div className="button-container">
        <CloseButton
          onClick={() => {
            uiActions.dismissModal();
          }}
        />
      </div>
    </div>
  );
};

export default ShareCardMenu;
